import React from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import { MdCheckCircle, MdStar, MdCheck } from "react-icons/md";
import { FaStar } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useJobCompletion } from "../../../JobCompletionContext";

const font = { fontFamily: "Poppins" };
const grayChip = {
  backgroundColor: "#E9E9E9",
  borderRadius: "20px",
  fontSize: "14px",
  fontWeight: 500,
};
const greenChip = {
  backgroundColor: "#00C23C",
  color: "#fff",
  borderRadius: "20px",
  fontSize: "14px",
  fontWeight: 500,
};

const ViewJob = () => {
  const location = useLocation();
  const job = location.state;
  const navigate = useNavigate();
  const { completedJobs } = useJobCompletion();

  const isJobDone = (jobName) => {
    console.log("job r", job.r);
    const sheetValue = (job.r?.[jobName] || "").trim().toLowerCase();
    const fromSheet = sheetValue === "" || sheetValue === "completed";
    const fromCache = completedJobs.has(jobName);
    return fromSheet || fromCache;
  };

  const allJobsCompleted =
    job.jobs &&
    job.jobs.length > 0 &&
    job.jobs.every((jobName) => isJobDone(jobName));

  const last = (array) => {
    return array[array.length - 1];
  };

  const services = [
    ...(job.r.Cleanup !== ""
      ? [
          {
            name: "Cleanup",
            highlight: job.r.Cleanup === "Completed",
            icon: <MdStar />,
          },
        ]
      : []),
    ...(job.r.Weeding !== ""
      ? [
          {
            name: "Weeding",
            highlight: job.r.Weeding === "Completed",
            icon: (
              <Typography
                fontSize="10px"
                fontWeight="600"
                fontFamily="Poppins"
                color="#000"
              >
                {job.r["Weeding Hours"]}
              </Typography>
            ),
          },
        ]
      : []),
    ...(job.r.Mulching !== ""
      ? [
          {
            name: "Mulching",
            highlight: job.r.Mulching === "Completed",
            color: last(job.r["Mulch Color"].split(" ")),
            mulch_color: job.r["Mulch Color"],
            icon: (
              <Typography
                fontSize="10px"
                fontWeight="600"
                fontFamily="Poppins"
                color="#fff"
                backgroundColor="#fff"
                borderRadius="20px"
                paddingLeft="3px"
                paddingRight="3px"
              >
                {job.r["Mulch Yards"]}
              </Typography>
            ),
          },
        ]
      : []),
    ...(job.r["Bush Trimming"] !== ""
      ? [
          {
            name: "Bush Trimming",
            highlight: job.r["Bush Trimming"] === "Completed",
            icon: (
              <Typography
                fontSize="10px"
                fontWeight="600"
                fontFamily="Poppins"
                color="#000"
              >
                {job.r["Bush Hours"]}
              </Typography>
            ),
          },
        ]
      : []),
    ...(job.r.Edging !== ""
      ? [
          {
            name: "Edging",
            highlight: job.r.Edging === "Completed",
            icon: <MdStar />,
          },
        ]
      : []),
    ...(job.r.Fertilizing !== ""
      ? [
          {
            name: "Fertilizing",
            highlight: job.r.Fertilizing === "Completed",
            icon: (
              <Typography
                fontSize="10px"
                fontWeight="600"
                fontFamily="Poppins"
                color="#000"
              >
                {job.r["Fertilizer Bags"]}
              </Typography>
            ),
          },
        ]
      : []),
    ...(job.r.Other !== ""
      ? [
          {
            name: "Other",
            highlight: job.r.Other === "Completed",
            icon: <MdStar />,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        ...font,
        width: "100%",
        maxWidth: 500,
        margin: "0 auto",
        minHeight: "100vh",
      }}
    >
      <Paper elevation={1} sx={{ p: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {job.address}
            </Typography>
            <Typography variant="body1">{job.cityState}</Typography>
          </Box>
          <Stack spacing={1} alignItems="flex-end">
            <Chip label={`Stop ${job.stopNumber}`} sx={grayChip} />
            <Chip label={job.bucket} sx={grayChip} />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* === Complete Jobs Section === */}
        <Box mb={2}>
          {/* Top Row: Label + Button */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Jobs
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                Complete these jobs:
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#00C23C",
                fontFamily: "Poppins",
                fontWeight: "bold",
                borderRadius: "10px",
                px: 3,
                "&:hover": { backgroundColor: "#00A830" },
              }}
              onClick={() =>
                navigate("/complete-jobs", {
                  state: {
                    stopNumber: job.stopNumber,
                    addressLong: job.addressLong,
                    address: job.address,
                    cityState: job.address.split(", ").slice(1).join(", "),
                    size: job.bucket || "Medium",
                    jobs: job.jobs,
                    r: job.r,
                    script_id: job.script_id,
                    image_upload_path: job.image_upload_path,
                    driver: job.driver,
                  },
                })
              }
            >
              {allJobsCompleted ? "Done" : "Complete Jobs"}
            </Button>
          </Box>

          {/* Chips Below */}
          <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
            {services.map((service, sidx) => {
              let name = service.name;
              if (name === "Mulching") {
                name = `${service.name} - ${service.mulch_color}`;
              }
              return (
                <Chip
                  key={sidx}
                  label={name}
                  sx={{
                    fontFamily: "Poppins",
                    px: 0.5,
                    py: 0.5,
                    fontSize: "10px",
                    fontWeight: "600",
                    backgroundColor: service.highlight
                      ? "#00C23C"
                      : service.color || "#E9E9E9",
                    color:
                      service.color && !service.highlight ? "#fff" : "#000",
                    borderRadius: "20px",
                  }}
                  icon={
                    service.highlight ? (
                      <MdCheck color={"#000"} />
                    ) : (
                      service.icon
                    )
                  }
                  variant="filled"
                />
              );
            })}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* === Quote Jobs Section === */}
        <Box mb={2}>
          {/* Top Row: Label + Button */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Quotes:
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                Complete these jobs:
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#52C41A",
                fontFamily: "Poppins",
                fontWeight: "bold",
                borderRadius: "10px",
                px: 3,
                "&:hover": { backgroundColor: "#389E0D" },
              }}
              onClick={() =>
                navigate("/quote-services", {
                  state: {
                    address: job.address,
                    bucket: job.bucket,
                    driver: job.driver,
                    script_id: job.script_id,
                    mulchingYards: job.r["Mulch Yards"] || "",
                    weedingHours: job.r["Weeding Hours"] || "",
                    bushTrimmingHours: job.r["Bush Hours"] || "",
                    fertilizationBags: job.r["Fertilizer Bags"] || "",
                  },
                })
              }
            >
              Quote Jobs
            </Button>
          </Box>

          {/* Chips Below */}
          <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
            {job.quotes.map((quoteName, idx) => (
              <Chip
                key={idx}
                label={quoteName}
                sx={{
                  backgroundColor: "#E9E9E9",
                  color: "#000",
                  borderRadius: "20px",
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "Poppins",
                }}
                icon={<FaStar style={{ fontSize: "16px" }} />}
              />
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notes Section */}
        <Typography variant="h6">Service Notes</Typography>
        <Typography variant="body2">{job.notes}</Typography>
        <Typography variant="body2">{job.notes_spanish}</Typography>
        <Typography variant="body2">{job.notes_portuguese}</Typography>

        {job.r.Other !== "" && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" mt={2}>
              Other Notes
            </Typography>
            <Typography variant="body2" fontFamily="Poppins">
              {job.r["Other Notes"] || "No additional notes provided."}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ViewJob;
